// Data-product GraphQL operations: IEX market prices, grid frequency, SLDC
// net-load and the live feed-health catalog.
// Typed manually (same idiom as queriesF2b.ts) because codegen cannot run until
// the data-product schema is folded into the committed graphql-bff SDL. Shape
// mirrors what graphql-codegen client-preset would emit; when codegen catches
// up, delete this file, move the operations to queries.ts and run
// `npm run codegen`.

import type { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { parse } from "graphql";

// ---------------------------------------------------------------------------
// Market prices — cleared IEX prices for one delivery date
// ---------------------------------------------------------------------------

/** "dam" | "rtm" | "gdam" | "hpdam" */
export type MarketSegment = "dam" | "rtm" | "gdam" | "hpdam";

export interface MarketPricePoint {
  market: string;
  blockNumber: number;
  time: string | null;
  mcpInrPerMwh: number;
  mcvMw: number | null;
}
export interface MarketPricesQuery {
  marketPrices: MarketPricePoint[];
}
export interface MarketPricesQueryVariables {
  deliveryDate: string;
  market?: string | null;
}

export const MarketPricesDocument: TypedDocumentNode<
  MarketPricesQuery,
  MarketPricesQueryVariables
> = parse(/* GraphQL */ `
  query MarketPrices($deliveryDate: String!, $market: String) {
    marketPrices(deliveryDate: $deliveryDate, market: $market) {
      market
      blockNumber
      time
      mcpInrPerMwh
      mcvMw
    }
  }
`) as TypedDocumentNode<MarketPricesQuery, MarketPricesQueryVariables>;

// ---------------------------------------------------------------------------
// Grid frequency — NRLDC 5-min scrape / POSOCO national batch
// ---------------------------------------------------------------------------

export interface GridFrequencyPoint {
  time: string;
  /** "nr" | "national" */
  region: string;
  hz: number;
}
export interface GridFrequencySeriesQuery {
  gridFrequencySeries: GridFrequencyPoint[];
}
export interface GridFrequencySeriesQueryVariables {
  minutes?: number | null;
  region?: string | null;
}

export const GridFrequencySeriesDocument: TypedDocumentNode<
  GridFrequencySeriesQuery,
  GridFrequencySeriesQueryVariables
> = parse(/* GraphQL */ `
  query GridFrequencySeries($minutes: Int, $region: String) {
    gridFrequencySeries(minutes: $minutes, region: $region) {
      time
      region
      hz
    }
  }
`) as TypedDocumentNode<GridFrequencySeriesQuery, GridFrequencySeriesQueryVariables>;

// ---------------------------------------------------------------------------
// SLDC net-load — state demand / net-load series (live scrape)
// ---------------------------------------------------------------------------

export interface NetloadPoint {
  time: string;
  stateCode: string;
  demandMw: number | null;
  netLoadMw: number | null;
  solarMw: number | null;
  windMw: number | null;
  drawalMw: number | null;
  frequencyHz: number | null;
}
export interface GridNetloadQuery {
  gridNetload: NetloadPoint[];
}
export interface GridNetloadQueryVariables {
  stateCode?: string | null;
  hours?: number | null;
}

export const GridNetloadDocument: TypedDocumentNode<
  GridNetloadQuery,
  GridNetloadQueryVariables
> = parse(/* GraphQL */ `
  query GridNetload($stateCode: String, $hours: Int) {
    gridNetload(stateCode: $stateCode, hours: $hours) {
      time
      stateCode
      demandMw
      netLoadMw
      solarMw
      windMw
      drawalMw
      frequencyHz
    }
  }
`) as TypedDocumentNode<GridNetloadQuery, GridNetloadQueryVariables>;

// ---------------------------------------------------------------------------
// Feed catalog — live health computed from the warehouse itself
// ---------------------------------------------------------------------------

/** Feed-health statuses returned by dataFeeds. */
export type FeedStatus = "live" | "partial" | "stale" | "idle" | "offline";

export interface DataFeedStatus {
  id: string;
  name: string;
  domain: string;
  cadence: string;
  status: FeedStatus;
  lastUpdate: string | null;
  records: number;
}
export interface DataFeedsQuery {
  dataFeeds: DataFeedStatus[];
}
export interface DataFeedsQueryVariables { [key: string]: never }

export const DataFeedsDocument: TypedDocumentNode<
  DataFeedsQuery,
  DataFeedsQueryVariables
> = parse(/* GraphQL */ `
  query DataFeeds {
    dataFeeds {
      id
      name
      domain
      cadence
      status
      lastUpdate
      records
    }
  }
`) as TypedDocumentNode<DataFeedsQuery, DataFeedsQueryVariables>;
